import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NS, PROFILE_KEYS } from '../../../i18n/constants/keys';
import { toast } from '../../../ui/toast';
import { updateStatus, updateUser } from '../../../utils/api_service/user_api';
import { RootState } from '../../../utils/redux_store/store';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { CareerStatus, PROFILE_SECTION_IDS, PROFILE_STORAGE_KEYS } from '../constants';
import { getDefaultNotifications, getUserInitials, readCareerStatusFromStorage, readIsEmployee, readProfileFormFromStorage, readUserRole } from '../functions';
import { getProfileSections } from '../functions/getProfileSections';

export interface IUseProfilePage {
  activeSection: string;
  setActiveSection: (section: string) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  loading: boolean;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  email: string;
  profileLink: string;
  setProfileLink: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  jobTitle: string;
  jobTitleId: string;
  handleJobTitleChange: (option: SelectOptions | null) => void;
  bio: string;
  setBio: (value: string) => void;
  profilePicStatus: string;
  isEmployee: boolean;
  selectedJobTitle: SelectOptions | null;
  jobTitleOptions: SelectOptions[];
  careerStatus: CareerStatus;
  handleCareerStatus: (status: CareerStatus) => Promise<void>;
  notifications: { id: string; labelKey: string; descriptionKey: string; enabled: boolean }[];
  userRole: string;
  userInitials: string;
  profileSections: { id: string; titleKey: string; descriptionKey: string }[];
  handleSave: () => Promise<void>;
  handleCancelEdit: () => void;
  handleStartEdit: () => void;
}

export const useProfilePage = (): IUseProfilePage => {
  const { t } = useTranslation(NS.profile);
  const initialForm = useMemo(() => readProfileFormFromStorage(), []);
  const [firstName, setFirstName] = useState<string>(initialForm.firstName);
  const [lastName, setLastName] = useState<string>(initialForm.lastName);
  const [email, setEmail] = useState<string>(initialForm.email);
  const [profileLink, setProfileLink] = useState<string>(initialForm.profileLink);
  const [phone, setPhone] = useState<string>(initialForm.phone);
  const [jobTitle, setJobTitle] = useState<string>(initialForm.jobTitle);
  const [jobTitleId, setJobTitleId] = useState<string>(initialForm.jobTitleId);
  const [bio, setBio] = useState<string>(initialForm.bio);
  const [profilePicStatus] = useState<string>(initialForm.profilePicStatus);

  const [activeSection, setActiveSection] = useState<string>(PROFILE_SECTION_IDS.PERSONAL);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [careerStatus, setCareerStatus] = useState<CareerStatus>(() => readCareerStatusFromStorage());
  const [notifications, setNotifications] = useState(getDefaultNotifications);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const userRole = useMemo(() => readUserRole(), []);
  const isEmployee = useMemo(() => readIsEmployee(), []);
  const jobTitleOptions = useSelector((state: RootState) => state.jobTitles.jobTitlesList);
  const selectedJobTitle: SelectOptions | null =
    jobTitleOptions.find((option) => option.value === jobTitleId)
    || (jobTitleId ? { value: jobTitleId, label: jobTitle } : null);

  const profileSections = useMemo(() => getProfileSections(userRole), [userRole]);
  const userInitials = getUserInitials(firstName, lastName);

  const handleCareerStatus = async (status: CareerStatus): Promise<void> => {
    try {
      const res = await updateStatus(status);
      if (res.ok && isMounted.current) {
        const response = await res.json();
        setCareerStatus(response.jobSeekingStatus);
        localStorage.setItem(PROFILE_STORAGE_KEYS.JOB_STATUS, response.jobSeekingStatus);
      }
    } catch (error) {
      console.error('Failed to update career status:', error);
    }
  };

  const handleJobTitleChange = (option: SelectOptions | null): void => {
    setJobTitleId(option?.value || '');
    setJobTitle(option?.label || '');
  };

  const handleSave = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await updateUser({
        firstName,
        lastName,
        phone,
        bio,
        ...(isEmployee ? { jobTitleId } : {}),
      });
      if (!isMounted.current) return;
      if (response?.error || !response?.id) {
        throw new Error(response?.error || 'update failed');
      }
      setFirstName(response.firstName || '');
      setLastName(response.lastName || '');
      setEmail(response.email || '');
      setPhone(response.phone || '');
      setBio(response.bio || '');
      setJobTitle(response.jobTitle || '');
      setJobTitleId(response.jobTitleId || '');
      localStorage.setItem(PROFILE_STORAGE_KEYS.FIRST_NAME, response.firstName || '');
      localStorage.setItem(PROFILE_STORAGE_KEYS.LAST_NAME, response.lastName || '');
      localStorage.setItem(PROFILE_STORAGE_KEYS.EMAIL, response.email || '');
      localStorage.setItem(PROFILE_STORAGE_KEYS.PHONE, response.phone || '');
      localStorage.setItem(PROFILE_STORAGE_KEYS.BIO, response.bio || '');
      localStorage.setItem(PROFILE_STORAGE_KEYS.JOB_TITLE, response.jobTitle || '');
      localStorage.setItem(PROFILE_STORAGE_KEYS.JOB_TITLE_ID, response.jobTitleId || '');

      window.dispatchEvent(
        new CustomEvent('userProfileUpdated', {
          detail: {
            firstName: response.firstName,
            lastName: response.lastName,
            email,
          },
        }),
      );
      window.dispatchEvent(new Event('storage'));

      toast.success(t(PROFILE_KEYS.messages.updateSuccess), { position: 'bottom-center' });
      setIsEditing(false);
    } catch (error) {
      if (!isMounted.current) return;
      toast.error(t(PROFILE_KEYS.messages.updateFailed), { position: 'bottom-center' });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleCancelEdit = (): void => {
    setIsEditing(false);
  };

  const handleStartEdit = (): void => {
    setIsEditing(true);
  };

  return {
    activeSection,
    setActiveSection,
    isEditing,
    setIsEditing,
    loading,
    isModalOpen,
    setIsModalOpen,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    profileLink,
    setProfileLink,
    phone,
    setPhone,
    jobTitle,
    jobTitleId,
    handleJobTitleChange,
    bio,
    setBio,
    profilePicStatus,
    isEmployee,
    selectedJobTitle,
    jobTitleOptions,
    careerStatus,
    handleCareerStatus,
    notifications,
    userRole,
    userInitials,
    profileSections,
    handleSave,
    handleCancelEdit,
    handleStartEdit,
  };
};
